import { Button, makeStyles, TextField, TextFieldProps, Typography } from '@material-ui/core'
import React, { useEffect, useRef, useState } from 'react'
import { useNewPhone, useVerifyPhone } from '../api'
import { useCaptcha } from '../Captcha'
import { strings } from '../strings'
import { LoadingButton } from '../LoadingButton'

interface OptionsType {
  userId: number
  phoneNumber: string
  onSuccess?: (phoneNumber: string) => void
  onCancel?: () => void
}

export function PhoneVerifying({ userId, phoneNumber, onSuccess, onCancel }: OptionsType) {
  const [step, setStep] = useState(1)
  const [phone, setPhone] = useState<string>(phoneNumber)

  const handleCancel = () => {
    if (onCancel) onCancel()
  }

  const handleSuccess = () => {
    if (onSuccess) onSuccess(phone)
  }

  if (step === 1)
    return (
      <StepOne
        phoneNumber={phone}
        onChange={(x: string) => setPhone(x)}
        onClose={handleCancel}
        onNextStep={() => setStep(2)}
      />
    )

  if (step === 2)
    return (
      <StepTwo
        userId={userId}
        onClose={handleCancel}
        onPrevStep={() => setStep(1)}
        onNextStep={() => setStep(3)}
      />
    )

  return <StepThree onClose={handleSuccess} />
}

function StepOne({
  phoneNumber,
  onChange: handleChange,
  onClose: handleClose,
  onNextStep: handleNextStep,
}: any) {
  const classes = useStyles()
  const newPhone = useNewPhone()
  const refPhone = useRef<HTMLInputElement>()
  const [captcha, refreshCaptcha, getCaptchaInfo] = useCaptcha()
  const [{ inProgress, error }, setState] = useState({
    inProgress: false,
    error: '',
  })

  const handleSendCode = () => {
    const val = refPhone.current?.value
    if (!val) return

    handleChange(val)
    setState({ inProgress: true, error: '' })

    newPhone(val, getCaptchaInfo())
      .then(() => {
        setState({ inProgress: false, error: '' })
        handleNextStep()
      })
      .catch((err) => {
        setState({ inProgress: false, error: err.message })
        refreshCaptcha()
      })
  }

  return (
    <>
      <h2>ارسال کد تایید به شماره موبایل</h2>
      <p>اگر شماره موبایل اشتباه است آن را تصحیح کنید</p>
      <CustomTextField
        defaultValue={phoneNumber}
        inputRef={refPhone}
        label={strings.phoneNumber}
        margin="normal"
        fullWidth
        autoFocus
      />
      {captcha}
      <Typography color="error">{error}</Typography>
      <div className={classes.btnsOne}>
        <div>
          <LoadingButton
            type="submit"
            variant="outlined"
            color="primary"
            onClick={handleSendCode}
            disabled={inProgress}
            loading={inProgress}
            text="ارسال کد تایید"
          />
        </div>
        <div>
          <Button type="button" variant="outlined" color="secondary" onClick={handleClose}>
            {strings.cancel}
          </Button>
        </div>
      </div>
    </>
  )
}

function StepTwo({
  userId,
  onClose: handleClose,
  onPrevStep: handlePrevStep,
  onNextStep: handleNextStep,
}: any) {
  const classes = useStyles()
  const verifyPhone = useVerifyPhone()
  const refVerify = useRef<HTMLInputElement>()
  const [timer, setTimer] = useState(60)
  const [{ inProgress, error }, setState] = useState({
    inProgress: false,
    error: '',
  })

  useEffect(() => {
    if (timer <= 0) return

    const id = setTimeout(() => {
      setTimer((prev) => prev - 1)
    }, 1000)

    return () => clearTimeout(id)
  })

  const handleConfirm = () => {
    const val = refVerify.current?.value
    if (!val) return

    setState({ inProgress: true, error: '' })

    verifyPhone(val, userId)
      .then(handleNextStep)
      .catch((err) => setState({ inProgress: false, error: err.message }))
  }

  return (
    <>
      <h2>تایید شماره موبایل</h2>
      <p>کدی که به موبایل شما پیامک می شود را اینجا وارد نمایید</p>
      <CustomTextField
        inputRef={refVerify}
        label={strings.confirmationCode}
        margin="normal"
        fullWidth
        autoFocus
      />
      <Typography color="error">{error}</Typography>
      <div className={classes.btnsTwo}>
        <div>
          <LoadingButton
            type="submit"
            variant="outlined"
            color="primary"
            onClick={timer > 0 ? handleConfirm : handlePrevStep}
            disabled={inProgress}
            loading={inProgress}
            text="تایید"
          />
        </div>
        <div style={{ alignSelf: 'center' }}>
          {timer > 0 && !inProgress && new Date(timer * 1000).toISOString().substr(14, 5)}
          {!timer && (
            <Button
              type="submit"
              variant="outlined"
              color="primary"
              disabled={inProgress}
              onClick={handlePrevStep}
            >
              <span>ارسال مجدد</span>
            </Button>
          )}
        </div>
        <div>
          <Button type="button" variant="outlined" color="secondary" onClick={handleClose}>
            {strings.cancel}
          </Button>
        </div>
      </div>
    </>
  )
}

function StepThree({ onClose: handleClose }: any) {
  const classes = useStyles()

  return (
    <>
      <h2>شماره موبایل شما تایید شد</h2>
      <div className={classes.btnsThree}>
        <Button type="button" variant="outlined" color="secondary" onClick={handleClose}>
          بستن
        </Button>
      </div>
    </>
  )
}

function CustomTextField(props: TextFieldProps) {
  return <TextField margin="dense" autoComplete="off" variant="outlined" {...props} />
}

const useStyles = makeStyles((theme) => ({
  modal: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    padding: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      width: `calc(100% - ${theme.spacing(2)}px)`,
      transform: 'none',
      top: 0,
      left: 0,
      margin: theme.spacing(1),
    },
    '&:focus': {
      outline: 'none',
    },
  },
  btnsOne: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridGap: theme.spacing(3),
    marginTop: theme.spacing(1),
    '& > div:last-child': {
      textAlign: 'right',
    },
  },
  btnsTwo: {
    display: 'grid',
    gridTemplateColumns: 'auto auto 1fr',
    gridGap: theme.spacing(3),
    marginTop: theme.spacing(1),
    '& > div:last-child': {
      textAlign: 'right',
    },
  },
  btnsThree: {
    marginTop: theme.spacing(1),
    textAlign: 'right',
  },
}))
